import React, { useState, useEffect } from "react";
import {
  Search,
  Loader,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Mail,
  Trash2,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Shield,
  FileText,
  Link2,
  Globe,
  MapPin,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api from "../api/client";

const GRADE_COLORS = {
  A: { text: "text-green-400", bg: "bg-green-900/20", border: "border-green-500" },
  B: { text: "text-emerald-400", bg: "bg-emerald-900/20", border: "border-emerald-500" },
  C: { text: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-500" },
  D: { text: "text-orange-400", bg: "bg-orange-900/20", border: "border-orange-500" },
  F: { text: "text-red-400", bg: "bg-red-900/20", border: "border-red-500" },
};

const STATUS_ICON = {
  pass: { icon: CheckCircle, color: "text-green-400" },
  warning: { icon: AlertCircle, color: "text-amber-400" },
  fail: { icon: XCircle, color: "text-red-400" },
};

const CATEGORY_META = {
  technical: { label: "Technical SEO", icon: Shield, weight: "25%" },
  onpage: { label: "On-Page SEO", icon: FileText, weight: "30%" },
  backlinks: { label: "Backlink Profile", icon: Link2, weight: "20%" },
  content: { label: "Content", icon: Globe, weight: "15%" },
  local: { label: "Local SEO", icon: MapPin, weight: "10%" },
};

function ScoreBar({ label, score, weight }) {
  const color = score >= 80 ? "bg-green-500" : score >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-400">{label} <span className="text-gray-600">({weight})</span></span>
        <span className="font-mono text-white">{score}/100</span>
      </div>
      <div className="w-full bg-dark-50 rounded-full h-2">
        <div className={`${color} rounded-full h-2 transition-all duration-500`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function FindingsSection({ title, checks, icon: Icon }) {
  const [expanded, setExpanded] = useState(true);
  if (!checks || checks.length === 0) return null;

  return (
    <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-dark-50/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-copper" />
          <span className="text-sm font-medium">{title}</span>
          <span className="text-[10px] text-gray-500 ml-2">
            {checks.filter((c) => c.status === "pass").length}/{checks.length} passed
          </span>
        </div>
        {expanded ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
      </button>
      {expanded && (
        <div className="border-t border-dark-50 divide-y divide-dark-50/50">
          {checks.map((check, i) => {
            const si = STATUS_ICON[check.status] || STATUS_ICON.warning;
            const IconComp = si.icon;
            return (
              <div key={i} className="px-4 py-3 flex items-start gap-3">
                <IconComp size={16} className={`${si.color} shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">{check.check}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{check.details}</div>
                </div>
                {check.severity && check.severity !== "info" && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                    check.severity === "critical" ? "bg-red-900/20 text-red-400" : "bg-amber-900/20 text-amber-400"
                  }`}>
                    {check.severity}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ======== AUDIT RESULTS VIEW ======== */
function AuditResults({ audit, onBack }) {
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(audit.report_emailed);
  const [emailInput, setEmailInput] = useState(audit.prospect_email || "");
  const [showEmail, setShowEmail] = useState(false);

  const grade = GRADE_COLORS[audit.grade] || GRADE_COLORS.F;
  const findings = audit.findings || {};

  const handleDownload = () => {
    api
      .get(`/seo-audit/${audit.id}/report/`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `seo-audit-${audit.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const handleEmail = () => {
    if (!emailInput) return;
    setEmailing(true);
    api.post(`/seo-audit/${audit.id}/email/`, { email: emailInput })
      .then(() => { setEmailSent(true); setShowEmail(false); })
      .catch(() => {})
      .finally(() => setEmailing(false));
  };

  return (
    <div className="space-y-6">
      {/* Back + actions */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-white transition-colors">
          &larr; Back to audits
        </button>
        <div className="flex items-center gap-2">
          <button onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 transition-colors">
            <Download size={16} /> Download PDF
          </button>
          {!emailSent ? (
            showEmail ? (
              <div className="flex items-center gap-2">
                <input
                  type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="prospect@email.com"
                  className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none w-56"
                />
                <button onClick={handleEmail} disabled={emailing || !emailInput}
                  className="flex items-center gap-1 px-3 py-2 bg-copper/20 text-copper rounded-lg text-sm font-medium hover:bg-copper/30 disabled:opacity-50">
                  {emailing ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
                  Send
                </button>
                <button onClick={() => setShowEmail(false)} className="text-gray-500 text-sm hover:text-white">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setShowEmail(true)}
                className="flex items-center gap-2 px-4 py-2 border border-copper/30 text-copper rounded-lg text-sm hover:bg-copper/10 transition-colors">
                <Mail size={16} /> Email Report
              </button>
            )
          ) : (
            <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle size={14} /> Emailed</span>
          )}
        </div>
      </div>

      {/* Score header */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-6">
        <div className="flex items-center gap-6">
          {/* Big score */}
          <div className={`w-28 h-28 rounded-full border-4 ${grade.border} flex items-center justify-center flex-col`}>
            <span className={`text-3xl font-bold font-mono ${grade.text}`}>{audit.overall_score}</span>
            <span className="text-[10px] text-gray-500">/ 100</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-4xl font-bold ${grade.text}`}>{audit.grade}</span>
              <div>
                <h2 className="text-lg font-medium text-white">{audit.domain}</h2>
                <a href={audit.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-copper flex items-center gap-1">
                  {audit.url} <ExternalLink size={10} />
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>{findings.pages_crawled || 0} pages crawled</span>
              <span>{findings.issues_found || 0} issues found</span>
              <span className={findings.critical_issues > 0 ? "text-red-400" : "text-green-400"}>
                {findings.critical_issues || 0} critical
              </span>
              {audit.prospect_name && <span>Prospect: {audit.prospect_name}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Category scores */}
      <div className="bg-dark-300 rounded-xl border border-dark-50 p-4 space-y-3">
        <ScoreBar label="Technical SEO" score={audit.technical_score} weight="25%" />
        <ScoreBar label="On-Page SEO" score={audit.onpage_score} weight="30%" />
        <ScoreBar label="Backlink Profile" score={audit.backlink_score} weight="20%" />
        <ScoreBar label="Content" score={audit.content_score} weight="15%" />
        <ScoreBar label="Local SEO" score={audit.local_score} weight="10%" />
      </div>

      {/* Detailed findings */}
      {Object.entries(CATEGORY_META).map(([key, meta]) => (
        <FindingsSection
          key={key}
          title={meta.label}
          checks={findings[key] || []}
          icon={meta.icon}
        />
      ))}
    </div>
  );
}

/* ======== MAIN COMPONENT ======== */
export default function SEOAuditPage() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [prospectName, setProspectName] = useState("");
  const [prospectEmail, setProspectEmail] = useState("");
  const [prospectPhone, setProspectPhone] = useState("");
  const [prospectCompany, setProspectCompany] = useState("");
  const [showProspect, setShowProspect] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeAudit, setActiveAudit] = useState(null);
  const [viewAudit, setViewAudit] = useState(null);
  const [progress, setProgress] = useState({
    pages_crawled: 0,
    pages_total: 0,
    current_phase: "",
  });

  const fetchAudits = () => {
    api.get("/seo-audits/")
      .then((res) => setAudits(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAudits(); }, []);

  const handleStart = () => {
    if (!url.trim()) return;
    setRunning(true);
    setProgress({ pages_crawled: 0, pages_total: 0, current_phase: "Starting" });
    api.post("/seo-audit/", {
      url: url.startsWith("http") ? url : `https://${url}`,
      prospect_name: prospectName,
      prospect_email: prospectEmail,
      prospect_phone: prospectPhone,
      prospect_company: prospectCompany,
    })
      .then((res) => {
        // If cached result returned, skip polling
        if (res.data.cached || res.data.status === "completed") {
          setRunning(false);
          setActiveAudit(null);
          setAudits((prev) => {
            const exists = prev.some((a) => a.id === res.data.id);
            return exists ? prev.map((a) => a.id === res.data.id ? res.data : a) : [res.data, ...prev];
          });
          setViewAudit(res.data);
          setUrl("");
          setProspectName("");
          setProspectEmail("");
          setProspectPhone("");
          setProspectCompany("");
          setShowProspect(false);
          return;
        }

        setActiveAudit(res.data);
        setAudits((prev) => [res.data, ...prev]);
        setUrl("");
        setProspectName("");
        setProspectEmail("");
        setProspectPhone("");
        setProspectCompany("");
        setShowProspect(false);

        // Poll the lightweight status endpoint for progress
        const poll = setInterval(() => {
          api.get(`/seo-audit/${res.data.id}/status/`)
            .then((r) => {
              setProgress({
                pages_crawled: r.data.pages_crawled || 0,
                pages_total: r.data.pages_total || 0,
                current_phase: r.data.current_phase || "",
              });

              if (r.data.status === "completed" || r.data.status === "failed") {
                clearInterval(poll);
                setRunning(false);
                setActiveAudit(null);
                // Fetch full audit data for the results view
                if (r.data.status === "completed") {
                  api.get(`/seo-audit/${res.data.id}/`).then((full) => {
                    setAudits((prev) => prev.map((a) => a.id === full.data.id ? full.data : a));
                    setViewAudit(full.data);
                  });
                } else {
                  fetchAudits();
                }
              }
            })
            .catch(() => {});
        }, 3000);
        // Safety timeout at 11 minutes (task has 10 min limit)
        setTimeout(() => { clearInterval(poll); setRunning(false); setActiveAudit(null); fetchAudits(); }, 660000);
      })
      .catch((err) => {
        setRunning(false);
        alert(err.response?.data?.error || err.response?.data?.detail || "Failed to start audit");
      });
  };

  const handleDelete = (id) => {
    api.delete(`/seo-audit/${id}/delete/`).then(() => {
      setAudits((prev) => prev.filter((a) => a.id !== id));
    });
  };

  const handleRerun = (audit) => {
    if (running) return;
    const auditUrl = audit.url || audit.domain;
    if (!auditUrl) return;
    setRunning(true);
    setProgress({ pages_crawled: 0, pages_total: 0, current_phase: "Starting" });
    api.post("/seo-audit/", {
      url: auditUrl.startsWith("http") ? auditUrl : `https://${auditUrl}`,
      prospect_name: audit.prospect_name || "",
      prospect_email: audit.prospect_email || "",
      prospect_phone: audit.prospect_phone || "",
      prospect_company: audit.prospect_company || "",
      force: true,
    })
      .then((res) => {
        if (res.data.cached || res.data.status === "completed") {
          setRunning(false);
          setActiveAudit(null);
          setAudits((prev) => {
            const exists = prev.some((a) => a.id === res.data.id);
            return exists ? prev.map((a) => a.id === res.data.id ? res.data : a) : [res.data, ...prev];
          });
          setViewAudit(res.data);
          return;
        }
        setActiveAudit(res.data);
        setAudits((prev) => [res.data, ...prev]);
        const poll = setInterval(() => {
          api.get(`/seo-audit/${res.data.id}/status/`)
            .then((r) => {
              setProgress({
                pages_crawled: r.data.pages_crawled || 0,
                pages_total: r.data.pages_total || 0,
                current_phase: r.data.current_phase || "",
              });
              if (r.data.status === "completed" || r.data.status === "failed") {
                clearInterval(poll);
                setRunning(false);
                setActiveAudit(null);
                if (r.data.status === "completed") {
                  api.get(`/seo-audit/${res.data.id}/`).then((full) => {
                    setAudits((prev) => prev.map((a) => a.id === full.data.id ? full.data : a));
                    setViewAudit(full.data);
                  });
                } else {
                  fetchAudits();
                }
              }
            })
            .catch(() => {});
        }, 3000);
        setTimeout(() => { clearInterval(poll); setRunning(false); setActiveAudit(null); fetchAudits(); }, 660000);
      })
      .catch((err) => {
        setRunning(false);
        alert(err.response?.data?.error || err.response?.data?.detail || "Failed to rerun audit");
      });
  };

  // If viewing an audit
  if (viewAudit) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-mono">
            <span className="text-copper">SEO</span> Audit
          </h1>
        </div>
        <AuditResults audit={viewAudit} onBack={() => setViewAudit(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-mono">
          <span className="text-copper">SEO</span> Audit
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Run a comprehensive SEO analysis on any website and generate a professional report
        </p>
      </div>

      {/* Start audit form */}
      <div className="bg-dark-300 rounded-xl border border-copper/30 p-6">
        <h2 className="text-sm font-medium text-white mb-4">New Audit</h2>
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Enter website URL (e.g., example.com)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleStart()}
              className="w-full bg-dark border border-dark-50 rounded-lg pl-10 pr-4 py-3 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none"
            />
          </div>
          <button
            onClick={handleStart}
            disabled={running || !url.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-copper text-dark rounded-lg font-medium text-sm hover:bg-copper/90 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {running ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
            {running ? "Auditing..." : "Run Audit"}
          </button>
        </div>

        {/* Prospect info toggle */}
        <button
          onClick={() => setShowProspect(!showProspect)}
          className="text-xs text-gray-500 hover:text-copper mt-3 flex items-center gap-1 transition-colors"
        >
          {showProspect ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {showProspect ? "Hide" : "Add"} prospect details (optional)
        </button>

        {showProspect && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <input type="text" placeholder="Prospect name" value={prospectName}
              onChange={(e) => setProspectName(e.target.value)}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
            <input type="email" placeholder="Email" value={prospectEmail}
              onChange={(e) => setProspectEmail(e.target.value)}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
            <input type="text" placeholder="Phone" value={prospectPhone}
              onChange={(e) => setProspectPhone(e.target.value)}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
            <input type="text" placeholder="Company" value={prospectCompany}
              onChange={(e) => setProspectCompany(e.target.value)}
              className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          </div>
        )}
      </div>

      {/* Running audit progress */}
      {running && activeAudit && (
        <div className="bg-dark-300 rounded-xl border border-copper/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Loader size={24} className="animate-spin text-copper flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">
                Auditing {activeAudit.domain}...
              </p>
              <p className="text-xs text-gray-500">
                {progress.current_phase || "Starting audit"}
                {progress.pages_total > 0 && (
                  <span className="ml-1 text-copper font-mono">
                    {progress.pages_crawled}/{progress.pages_total} pages
                  </span>
                )}
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-dark-50 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-copper rounded-full h-2.5 transition-all duration-700 ease-out"
              style={{
                width: progress.pages_total > 0
                  ? `${Math.min(
                      (progress.pages_crawled / progress.pages_total) * 100,
                      100
                    )}%`
                  : "5%",
              }}
            />
          </div>
          <p className="text-[10px] text-gray-600 mt-2 text-right">
            {progress.pages_total > 0
              ? `${Math.round((progress.pages_crawled / progress.pages_total) * 100)}%`
              : "Discovering pages..."}
          </p>
        </div>
      )}

      {/* Audit history */}
      <div>
        <h2 className="text-sm font-medium text-gray-400 mb-3">Audit History</h2>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader className="animate-spin text-copper" size={24} />
          </div>
        ) : audits.length === 0 ? (
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-12 text-center text-gray-500">
            <Search size={32} className="mx-auto mb-3 text-gray-600" />
            <p>No audits yet.</p>
            <p className="text-xs mt-1">Enter a URL above to run your first SEO audit.</p>
          </div>
        ) : (
          <div className="bg-dark-300 rounded-xl border border-dark-50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-50 text-left">
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Domain</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Score</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Grade</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Issues</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Prospect</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Date</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium">Status</th>
                    <th className="px-4 py-3 text-xs text-gray-500 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {audits.map((audit) => {
                    const gc = GRADE_COLORS[audit.grade] || GRADE_COLORS.F;
                    return (
                      <tr key={audit.id} className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => audit.status === "completed" && setViewAudit(audit)}
                            className="text-copper hover:underline text-sm font-medium"
                            disabled={audit.status !== "completed"}
                          >
                            {audit.domain}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono text-white">{audit.status === "completed" ? audit.overall_score : "—"}</td>
                        <td className="px-4 py-3">
                          {audit.grade ? (
                            <span className={`text-sm font-bold ${gc.text}`}>{audit.grade}</span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono">
                          {audit.findings?.issues_found ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs truncate max-w-[120px]">
                          {audit.prospect_name || audit.prospect_email || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                          {new Date(audit.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          {audit.status === "completed" ? (
                            <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Done</span>
                          ) : audit.status === "running" || audit.status === "pending" ? (
                            <span className="text-xs text-blue-400 flex items-center gap-1"><Loader size={12} className="animate-spin" /> Running</span>
                          ) : (
                            <span className="text-xs text-red-400 flex items-center gap-1"><XCircle size={12} /> Failed</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {audit.status === "completed" && (
                              <>
                                <button
                                  onClick={() => setViewAudit(audit)}
                                  className="p-1 text-gray-500 hover:text-copper transition-colors" title="View"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    api.get(`/seo-audit/${audit.id}/report/`, { responseType: "blob" })
                                      .then((res) => {
                                        const url = window.URL.createObjectURL(new Blob([res.data]));
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = `SEO-Audit-${audit.domain || audit.id}.pdf`;
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        window.URL.revokeObjectURL(url);
                                      })
                                      .catch(() => {});
                                  }}
                                  className="p-1 text-gray-500 hover:text-copper transition-colors" title="Download PDF"
                                >
                                  <Download size={14} />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleRerun(audit)}
                              disabled={running}
                              className="p-1 text-gray-500 hover:text-blue-400 transition-colors disabled:opacity-30" title="Rerun Audit"
                            >
                              <RefreshCw size={14} />
                            </button>
                            {audit.report_emailed && (
                              <span className="p-1 text-green-500" title="Report emailed"><Mail size={14} /></span>
                            )}
                            <button
                              onClick={() => handleDelete(audit.id)}
                              className="p-1 text-gray-600 hover:text-red-400 transition-colors" title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
